import { useEffect, useState } from "react";
import { set, useForm } from "react-hook-form";
import { data, useLocation, useNavigate } from "react-router-dom";
import MediaUpload from "../../components/Forms/MediaUpload";
import { ActionButton } from "../../components/Forms/ActionButton";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useCreateBadge, useCreateBadgeCategory, useGetBadgeCategoryDetails, useUpdateBadgeCategory } from "../../queries/badge";

const CreateBadgeCategory = () => {

  const navigate = useNavigate();
  const { state } = useLocation();
  const id = state?.id;

  const [active, setActive] = useState(true);
  // Parameters
  const [points, setPoints] = useState(false);
  const [carbon, setCarbon] = useState(false);
  const [trees, setTrees] = useState(false);
  const [challenges, setChallenges] = useState(false);
  const [events, setEvents] = useState(false);
  const [donations, setDonations] = useState(false);
  const [friends, setFriends] = useState(false);
  const [coverImage, setCoverImage] = useState(null);
  const [mediaClear, setMediaClear] = useState(false);

  const { data: factDetails, isLoading } = useGetBadgeCategoryDetails(id);

  const { mutateAsync, isPending: isCreating } = useCreateBadgeCategory();
  const { mutateAsync: updateFact, isPending: isEditing } = useUpdateBadgeCategory();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      name: factDetails ? factDetails.name : "",
      description: factDetails ? factDetails.description : "",
    }
  });

  const onSubmit = async (data) => {
    if(!coverImage) {
      toast.error('Please Upload Cover Image!');
      return;
    }
    console.log({
      name: data.name,
      description: data.description,
      file: coverImage,
      isActive: active,
      points: points,
      carbon: carbon,
      trees: trees,
      challenges: challenges,
      events: events,
      donations: donations,
      friends: Boolean(friends),
    });

    if (id) {
      toast.loading('Updating Badge Category...');
      updateFact({
        id: id,
        name: data.name,
        description: data.description,
        file: coverImage,
        isActive: active,
        points: points,
        carbon: carbon,
        trees: trees,
        challenges: challenges,
        events: events,
        donations: donations,
        friends: Boolean(friends),
      }, {
        onSuccess: (res) => {
          if(res.error) {
            toast.dismiss();
            toast.error('Something Went Wrong!');
            return;
          }
          toast.dismiss();
          toast.success('Badge Category Updated Successfully!');
          reset();
          setCoverImage(null);
          setTimeout(() => {
            navigate('/badge/badge-categories/view-categories');
          }, 2000);
        },
        onError: (err) => {
          toast.dismiss();
          toast.error('Something Went Wrong!');
        }
      });
    } else {
      toast.loading('Creating Badge Category...');
      mutateAsync({
        name: data.name,
        description: data.description,
        file: coverImage,
        isActive: active,
        points: points,
        carbon: carbon,
        trees: trees,
        challenges: challenges,
        events: events,
        donations: donations,
        friends: Boolean(friends),
      }, {
        onSuccess: (res) => {
          if (res?.error) {
            toast.dismiss();
            toast.error('Something Went Wrong!');
            return;
          }
          toast.dismiss();
          toast.success('Badge Category Created Successfully!');
          setTimeout(() => {
            navigate('/badge/badge-categories/view-categories');
          }, 2000);
        },
        onError: (err) => {
          toast.dismiss();
          toast.error('Something Went Wrong!');
        }
      });
    }
  }

  useEffect(() => {
    if (factDetails) {
      setValue("name", factDetails?.name);
      setValue("description", factDetails?.description);
      setActive(factDetails?.isActive);
      setCoverImage(factDetails?.imageUrl);
      setPoints(factDetails?.points);
      setCarbon(factDetails?.carbon);
      setTrees(factDetails?.trees);
      setChallenges(factDetails?.challenges);
      setEvents(factDetails?.events);
      setDonations(factDetails?.donations);
      setFriends(factDetails?.friends);
    }
  }, [factDetails, isLoading]);

  useEffect(() => {
    setTimeout(() => {
      if (factDetails) {
        setCoverImage(factDetails.imageUrl);
        setValue("description", factDetails.description || "");
      }
    }, 500);
  }, [factDetails]);

  useEffect(() => {
    setMediaClear(false);
  }, [coverImage]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <form className="flex flex-col w-full font-semibold " onSubmit={handleSubmit(onSubmit)}>
      <section className="flex flex-col justify-center px-5 pt-2 py-9 gap-3 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)]">
        <div className="flex items-center justify-between">
          <p className="text-base font-bold mt-3">Badge Category Information</p>
          <div className="flex gap-3 items-center">
            <p className="text-base">Status:</p>
            <div className={`flex justify-end items-center border ${active ? `border-primary` : `border-inactive`} rounded-md h-full min-h-8 min-w-20`}>
              <label className="inline-flex items-center me-5 cursor-pointer h-full">
                <span className={`ms-3 text-base ${active ? `text-primary` : 'text-inactive'} font-bold dark:text-gray-300 mr-2`}>{active ? 'Active' : 'Inactive'}</span>
                <input type="checkbox" name={'active'} value={active} className="sr-only peer" onChange={() => setActive(!active)} checked={active} />
                <div className="relative w-8 h-4 bg-white border-black ring-1 ring-inactive rounded-full peer dark:bg-white peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.1 after:start-[0px] after:bg-inactive after:border-inactive peer-checked:after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-navBtn peer-checked:ring-primary peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <div className="flex flex-col w-full gap-4">
            {/* Category Name */}
            <div className="flex flex-col basis-0 min-h-[65px] w-full mt-1.5">
              <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Category Name</label>
              <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <input
                  type="text"
                  placeholder="Category Name"
                  {...register("name", { required: "Name is required" })}
                  className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.name ? "border-red-500" : ""}`}
                />
              </div>
              {errors.name && <p className="text-red text-sm">{errors.name.message}</p>}
            </div>
            {/* Select page */}
            <div className="flex flex-col flex-1 grow shrink-0 basis-0 w-full mt-1.5">
              <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Select Badge Parameters</label>
              <div className="flex items-center flex-wrap py-2 mt-1 min-h-10 px-3 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400 gap-5">
                <div className="flex items-center gap-2 basis-1/6">
                  <input type="checkbox" name="points" id="" value={points} checked={points || false} onChange={() => setPoints(!points)} />
                  <span className="ml-2">Points</span>
                </div>
                <div className="flex items-center gap-2 basis-1/6">
                  <input type="checkbox" name="carbon" id="" value={carbon} checked={carbon || false} onChange={() => setCarbon(!carbon)} />
                  <span className="ml-2">Carbon</span>
                </div>
                <div className="flex items-center gap-2 basis-1/6">
                  <input type="checkbox" name="trees" id="" value={trees} checked={trees || false} onChange={() => setTrees(!trees)} />
                  <span className="ml-2">Trees</span>
                </div>
                <div className="flex items-center gap-2 basis-1/6">
                  <input type="checkbox" name="challenges" id="" value={challenges} checked={challenges || false} onChange={() => setChallenges(!challenges)} />
                  <span className="ml-2">Challenges</span>
                </div>
                <div className="flex items-center gap-2 basis-1/6">
                  <input type="checkbox" name="events" id="" value={events} checked={events || false} onChange={() => setEvents(!events)} />
                  <span className="ml-2">Events</span>
                </div>
                <div className="flex items-center gap-2 basis-1/6">
                  <input type="checkbox" name="donations" id="" value={donations} checked={donations || false} onChange={() => setDonations(!donations)} />
                  <span className="ml-2">Donations</span>
                </div>
                <div className="flex items-center gap-2 basis-1/6">
                  <input type="checkbox" name="friends" id="" value={friends} checked={friends || false} onChange={() => setFriends(!friends)} />
                  <span className="ml-2">Friends</span>
                </div>
              </div>
            </div>
            {/* Description */}
            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[150px] w-full mt-1.5">
              <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Description</label>
              <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <textarea
                  type="text"
                  placeholder="Enter Description of Badge Category"
                  {...register("description", { required: "Description is required" })}
                  className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.description ? "border-red-500" : ""}`}
                  rows={4}
                />
              </div>
              {errors.description && <p className="text-red text-sm">{errors.description.message}</p>}
            </div>
          </div>
          {/* Cover Image */}
          <div className="mt-0">
            <MediaUpload
              clearMedia={mediaClear}
              label={"Thumbnail"}
              existingImage={coverImage}
              setImageUpload={setCoverImage}
              stateImage={coverImage}
              setStateImageUpload={setCoverImage}
              useFileObject={true}
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="mt-10 flex gap-5 items-center justify-between">
          <ActionButton
            variant="reset"
            text="Reset"
            handleSubmit={() => {
              reset();
              setCoverImage(null);
              setMediaClear(true);
              setActive(true);
              setPoints(false);
              setCarbon(false);
              setTrees(false);
              setChallenges(false);
              setEvents(false);
              setDonations(false);
              setFriends(false);
            }}
          />
          <div className="flex items-center gap-3">
            <ActionButton
              variant="cancel"
              text="Cancel"
              handleSubmit={() => navigate('/badge/badge-categories/view-categories')}
            />
            <ActionButton
              variant="submit"
              text="Save"
              // onClick={() => reset()}
              disabled={isCreating
                // || isEditingBlog
              }
            />
          </div>
        </div>
      </section>
      <ToastContainer
        position="top-right"
      />
    </form>
  );
}

export default CreateBadgeCategory;